import { EventEmitter, Injectable, Output } from '@angular/core';
import { ComponentController, filterEmpty } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { AppSharedApplicationPort } from 'application/common/port/app-shared-application.port';
import { AddCoachMeetingDialogComponent } from 'dialog/add-coach-meeting-dialog/add-coach-meeting-dialog.component';
import { CoacheesEditObserversComponent } from 'presentation/app-sidebar/sidebar-coachees/coachees-edit-observers/coachees-edit-observers.component';
import { SidebarCoachee } from 'presentation/app-sidebar/sidebar-coachees/sidebar-coachees.models';
import {
	SidebarCoacheesState,
	SidebarCoacheesStore,
} from 'presentation/app-sidebar/sidebar-coachees/sidebar-coachees.store';
import { UserApplicationPort } from 'user/port/user-application.port';

@Injectable()
export class SidebarCoacheesController extends ComponentController<SidebarCoacheesState> {
	public openPlan = new EventEmitter<string>();
	public openChat = new EventEmitter<string>();

	public coachees$: Observable<SidebarCoachee[]>;
	public searchTerm$: Observable<string>;

	constructor(
		store: SidebarCoacheesStore,
		private _dialogService: DialogService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _appSharedApplicationPort: AppSharedApplicationPort
	) {
		super(store);

		this._initObservables();
	}

	public updateSearchTerm(value: string) {
		this.store.update({ searchTerm: value });
	}

	private _initObservables() {
		this.searchTerm$ = this.select('searchTerm').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.coachees$ = this._userApplicationPort.observeCoachCoachees().pipe(
			map((data) => data.data),
			filterEmpty(),
			switchMap((coachees) =>
				this._appSharedApplicationPort
					.observePendingMessages(coachees.map((coachee) => coachee.id))
					.pipe(
						map((data) => data.data),
						map((pm) =>
							coachees.map(
								(coachee) =>
									({
										menu: [
											{
												icon: 'fas fa-eye',
												command: () =>
													this._dialogService.open(CoacheesEditObserversComponent, {
														header: this._translateService.instant(
															'presentation.app.sidebar.coachees.editObservers.header',
															{ name: coachee.name }
														),
														data: { coacheeId: coachee.id },
														style: { maxWidth: '80vw' },
														dismissableMask: true,
														width: '400px',
														modal: true,
													}),
											},
											{
												icon: 'fas fa-calendar-plus',
												command: () =>
													this._dialogService.open(AddCoachMeetingDialogComponent, {
														header: this._translateService.instant(
															'presentation.app.common.dialog.addCoachMeeting.title',
															{ name: coachee.name }
														),
														data: { coacheeId: coachee.id },
														style: { maxWidth: '80vw' },
														dismissableMask: true,
														width: '400px',
														modal: true,
													}),
											},
											{
												icon: 'fas fa-sitemap',
												command: () => this.openPlan.emit(coachee.id),
											},
											{
												icon: 'fas fa-comments',
												command: () => this.openChat.emit(coachee.id),
											},
										],
										pendingMessages: (pm || {})[coachee.id] || 0,
										entity: coachee,
									} as SidebarCoachee)
							)
						)
					)
			),
			switchMap((coachees) =>
				this.searchTerm$.pipe(
					map((term) =>
						coachees.filter(
							(coachee) =>
								!term || coachee.entity.name.toLowerCase().includes(term.toLowerCase())
						)
					)
				)
			),
			startWith([]),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}
}
